<script setup lang="ts">
import {ButtonType, Component, State} from "../types.ts";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import useStore from "../store.ts";
import {useRouter} from "vue-router";

const {item} = defineProps<{
  item: Component
}>();

let selectionBackground = "inherit";
let selectionForeground = "inherit";
let disabledBackground = "inherit";
let disabledForeground = "inherit";
let pressedBackground = "inherit";
let pressedForeground = "inherit";


if (item.button_props) {
  if (item.button_props.selection_colors) {
    selectionBackground = item.button_props.selection_colors.background ?? "inherit";
    selectionForeground = item.button_props.selection_colors.foreground ?? "inherit";
  }

  if (item.button_props.disabled_colors) {
    disabledBackground = item.button_props.disabled_colors.background ?? "inherit";
    disabledForeground = item.button_props.disabled_colors.foreground ?? "inherit";
  }

  if (item.button_props.pressed_colors) {
    pressedBackground = item.button_props.pressed_colors.background ?? "inherit";
    pressedForeground = item.button_props.pressed_colors.foreground ?? "inherit";
  }
}

const stateToPseudo: Partial<Record<State, string>> = {
  [State.ACTIVE]: ":active",
  [State.CHECKED]: ":checked",
  [State.DISABLED]: ":disabled",
  [State.EMPTY]: ":empty",
  [State.ENABLED]: ":enabled",
  [State.FOCUS]: ":focus",
  [State.HOVER]: ":hover",
  [State.INVALID]: ":invalid",
  [State.LINK]: ":link",
  [State.OPTIONAL]: ":optional",
  [State.REQUIRED]: ":required",
  [State.VALID]: ":valid",
  [State.VISITED]: ":visited",
};

const buttonClass = computed(
  () => `data-object-button-${(item.props?.id || "button").replace(/[^a-zA-Z0-9_-]/g, "-")}`
);

// see docs/EXTRACTED_COMMENTS.md#c332
const toImportant = (css: string) =>
  css
    .split(";")
    .map((decl) => {
      const idx = decl.indexOf(":");
      if (idx < 0) return "";
      const prop = decl.slice(0, idx).trim();
      let value = decl.slice(idx + 1).trim();
      if (!prop || !value) return "";
      if (!/!important$/.test(value)) value = `${value} !important`;
      return `${prop}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");

const stateStylesCSS = computed(() => {
  const rules: string[] = [];

  const baseCss = toImportant(item.button_props?.css_style ?? "");
  if (baseCss) {
    rules.push(`.${buttonClass.value}{${baseCss}}`);
  }

  for (const {state, css_style} of item.button_props?.state_style || []) {
    const pseudo = stateToPseudo[state];
    if (!pseudo || !css_style) continue;
    rules.push(`.${buttonClass.value}${pseudo}{${css_style}}`);
  }

  return rules.join("\n").trim();
});

const styleEl = ref<HTMLStyleElement | null>(null);

const ensureStyleEl = () => {
  if (styleEl.value) return styleEl.value;
  const el = document.createElement("style");
  el.dataset.buttonStyle = item.props?.id || "button";
  document.head.appendChild(el);
  styleEl.value = el;
  return el;
};

onMounted(() => {
  if (!stateStylesCSS.value) return;
  const el = ensureStyleEl();
  el.textContent = stateStylesCSS.value;
});

watch(stateStylesCSS, (css) => {
  if (!css) {
    if (styleEl.value?.parentNode) {
      styleEl.value.parentNode.removeChild(styleEl.value);
    }
    styleEl.value = null;
    return;
  }
  const el = ensureStyleEl();
  el.textContent = css;
});

onBeforeUnmount(() => {
  if (styleEl.value?.parentNode) {
    styleEl.value.parentNode.removeChild(styleEl.value);
  }
  styleEl.value = null;
});

onMounted(() => {
  const store = useStore();
  store.socketManager.requestButtonStatus(item);
});



const HOME_PAGE = import.meta.env.VITE_HOME_PAGE || "localhost:5000";
const router = useRouter();

const handleClick = async () => {
  const store = useStore();
  switch (item.button_props.button_type) {
    case ButtonType.ButtonUpdate: {
      store.socketManager.requestButtonUpdate(item, "ActionPressed");
      break;
    }
    case ButtonType.NavButtonHomePage: {
      if (isNaN(HOME_PAGE)) return;
      await router.push({path: `/${HOME_PAGE}`});
      store.layout = Number(HOME_PAGE);
      break;
    }
    case ButtonType.NavButtonSelectPage: {
      await router.push({path: `/${item.button_props.select_page}`});
      store.layout = Number(item.button_props.select_page);
      break;
    }
    case ButtonType.NavButtonPageNext: {
      store.socketManager.requestPageUp();
      break;
    }
    case ButtonType.NavButtonPagePrev: {
      store.socketManager.requestPageDown();
      break;
    }
    case ButtonType.NavButtonSubPageNext: {
      if (store.cluster.enabled) return;
      if (store.socketManager.layoutManager == null) return;
      if (store.socketManager.layoutManager.getRowsUsed() == null) return;
      /* see docs/EXTRACTED_COMMENTS.md#c752 */
      const strideUp = store.socketManager.layoutManager.getRowsUsed()!.start_row;
      if (strideUp <= 0) {
        console.warn(`[Button] cannot page forward: no row stride known yet (rows used = ${strideUp})`);
        return;
      }
      store.cluster.row_start = (store.cluster.row_start ?? 0) + strideUp;
      store.cluster.channel = store.socketManager.layoutManager.getRowsUsed()!.channel;

      await store.socketManager.layoutManager.updateRowStart();
      console.log(`[Button] Cluster datasheet will now start @ ${store.cluster.row_start}`);
      break;
    }
    case ButtonType.NavButtonSubPagePrev: {
      if (store.cluster.enabled) return;
      if (store.socketManager.layoutManager == null) return;
      if (store.socketManager.layoutManager.getRowsUsed() == null) return;
      /* see docs/EXTRACTED_COMMENTS.md#c752 */
      const strideDown = store.socketManager.layoutManager.getRowsUsed()!.start_row;
      if (strideDown <= 0) {
        console.warn(`[Button] cannot page back: no row stride known yet (rows used = ${strideDown})`);
        return;
      }
      store.cluster.row_start = Math.max(0, (store.cluster.row_start ?? 0) - strideDown);
      store.cluster.channel = store.socketManager.layoutManager.getRowsUsed()!.channel;
      await store.socketManager.layoutManager.updateRowStart();
      console.log(`[Button] Cluster datasheet will now start @  ${store.cluster.row_start}`);
      break;
    }
  }
};

</script>
<template>
  <button
      :class="buttonClass"
      :style="item.props.style"
      :disabled="item.button_props.disabled"
      @click="handleClick"
  >
    <p :id="`button-${item.props.id}`"
       :style="{textAlign: item.props.style['text-align']}"
       v-html="item.published.src">
    </p>
    <slot>
    </slot>
  </button>
</template>

<style scoped>
div {
  cursor: pointer;
}

button:focus {
  background: v-bind(selectionBackground) !important;
  color: v-bind(selectionForeground) !important;
}

button:disabled {
  background: v-bind(disabledBackground) !important;
  color: v-bind(disabledForeground) !important;
}

button:active {
  background: v-bind(pressedBackground) !important;
  color: v-bind(pressedForeground) !important;
}

button p {
  margin: 0;
  margin-block: 0;
  padding: 0;
  left: 0;
  right: 0;
  top: 0;
  position: relative;
}

</style>